import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Shield,
  Users,
  Activity,
  Database,
  TrendingUp,
  RefreshCw,
  ArrowLeft,
  CreditCard,
  Clock,
  AlertCircle,
  BarChart3,
  UserCog,
  Coins
} from "lucide-react";
import { Link } from "react-router-dom";
import { ErrorLogsTable } from "@/components/admin/ErrorLogsTable";
import { ReportsCharts } from "@/components/admin/ReportsCharts";
import { PurchasesManager } from "@/components/admin/PurchasesManager";
import { UserManager } from "@/components/admin/UserManager";
import { TaskMonitor } from "@/components/admin/TaskMonitor";
import { MetricTrendChart } from "@/components/admin/MetricTrendChart";
import { AdminActivityFeed } from "@/components/admin/AdminActivityFeed";
import { ICPAnalytics } from "@/components/admin/ICPAnalytics";
import { CreditManagement } from "@/components/admin/CreditManagement";

interface SystemStats {
  totalUsers: number;
  totalTasks: number;
  totalCredits: number;
  activeTasks: number;
  completedTasks: number;
  failedTasks: number;
  totalPurchases: number;
}

interface KieCredits {
  credits: number;
  fetchedAt: string;
  error?: string;
}

interface RecentUser {
  id: string;
  user_id: string;
  full_name: string | null;
  created_at: string;
  credits?: { available_credits: number } | null;
}

interface RecentTask {
  id: string;
  product_name: string;
  status: string;
  created_at: string;
  profiles: { full_name: string | null } | null;
}

interface RecentPurchase {
  id: string;
  credits_added: number;
  amount_paid: number | null;
  payment_status: string;
  created_at: string;
  profiles: { full_name: string | null } | null;
}

export default function AdminDashboard() {
  const { toast } = useToast();
  const { session } = useAuth();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState<SystemStats>({
    totalUsers: 0,
    totalTasks: 0,
    totalCredits: 0,
    activeTasks: 0,
    completedTasks: 0,
    failedTasks: 0,
    totalPurchases: 0,
  });
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [recentTasks, setRecentTasks] = useState<RecentTask[]>([]);
  const [recentPurchases, setRecentPurchases] = useState<RecentPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kieCredits, setKieCredits] = useState<KieCredits | null>(null);
  const [loadingKieCredits, setLoadingKieCredits] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setRefreshing(true);
    await Promise.all([
      loadSystemStats(),
      loadRecentUsers(),
      loadRecentTasks(),
      loadRecentPurchases(),
      loadKieCredits(),
    ]);
    setRefreshing(false);
    setLoading(false);
  };

  const loadSystemStats = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: { action: "get_system_stats" },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (error) throw error;

      setStats({
        totalUsers: data.totalUsers || 0,
        totalTasks: data.totalTasks || 0,
        totalCredits: data.totalCredits || 0,
        activeTasks: data.activeTasks || 0,
        completedTasks: data.completedTasks || 0,
        failedTasks: data.failedTasks || 0,
        totalPurchases: data.totalPurchases || 0,
      });
    } catch (error) {
      console.error("Error loading stats:", error);
      toast({
        title: "Erro ao carregar estatísticas",
        description: "Não foi possível carregar as estatísticas do sistema.",
        variant: "destructive",
      });
    }
  };

  const loadRecentUsers = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: { action: "get_all_users", limit: 5 },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (error) throw error;
      setRecentUsers(data?.users || []);
    } catch (error) {
      console.error("Error loading recent users:", error);
    }
  };

  const loadRecentTasks = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: { action: "get_all_tasks", limit: 5 },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (error) throw error;
      setRecentTasks(data?.tasks || []);
    } catch (error) {
      console.error("Error loading recent tasks:", error);
    }
  };

  const loadRecentPurchases = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: { action: "get_all_purchases", limit: 15 },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (error) throw error;
      setRecentPurchases(data?.purchases || []);
    } catch (error) {
      console.error("Error loading recent purchases:", error);
    }
  };

  const loadKieCredits = async () => {
    setLoadingKieCredits(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-kie-credits");

      if (error) throw error;

      if (data.error) {
        setKieCredits({ credits: 0, fetchedAt: new Date().toISOString(), error: data.error });
        toast({
          title: "Erro ao carregar créditos Kie.ai",
          description: data.error,
          variant: "destructive",
        });
      } else {
        setKieCredits({ credits: data.credits, fetchedAt: data.fetchedAt });
      }
    } catch (error) {
      console.error("Error loading Kie.ai credits:", error);
      setKieCredits({
        credits: 0,
        fetchedAt: new Date().toISOString(),
        error: error instanceof Error ? error.message : "Erro desconhecido"
      });
      toast({
        title: "Erro ao carregar créditos Kie.ai",
        description: "Não foi possível conectar com a API Kie.ai",
        variant: "destructive",
      });
    } finally {
      setLoadingKieCredits(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "failed":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "processing":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Shield className="w-12 h-12 text-primary animate-pulse" />
          <p className="text-muted-foreground">Carregando painel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">Painel Administrativo</h1>
                <p className="text-sm text-muted-foreground">Gerencie e monitore o sistema</p>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={loadAllData}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Usuários</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUsers}</div>
              <p className="text-xs text-muted-foreground mt-1">Total cadastrados</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tasks</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalTasks}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.completedTasks} ok · {stats.activeTasks} ativas · {stats.failedTasks} falhas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Créditos</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalCredits.toLocaleString("pt-BR")}</div>
              <p className="text-xs text-muted-foreground mt-1">Total no sistema</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxa de Sucesso</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.totalTasks > 0
                  ? Math.round((stats.completedTasks / stats.totalTasks) * 100)
                  : 0}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">Tasks completadas</p>
            </CardContent>
          </Card>
        </div>

        {/* ICP Analytics */}
        <ICPAnalytics />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Créditos Kie.ai</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadKieCredits}
                disabled={loadingKieCredits}
              >
                <RefreshCw className={`w-4 h-4 ${loadingKieCredits ? "animate-spin" : ""}`} />
              </Button>
            </CardTitle>
            <CardDescription>Créditos disponíveis na plataforma Kie.ai</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingKieCredits ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : kieCredits ? (
              <div className="space-y-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-primary">
                    {kieCredits.credits.toLocaleString("pt-BR")}
                  </span>
                  <span className="text-sm text-muted-foreground">créditos</span>
                </div>
                {kieCredits.error ? (
                  <div className="flex items-center gap-2 text-sm text-destructive">
                    <AlertCircle className="w-4 h-4" />
                    <span>{kieCredits.error}</span>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Atualizado em {formatDate(kieCredits.fetchedAt)}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">Clique em atualizar para carregar</p>
            )}
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {isMobile ? (
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione uma seção" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="overview">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Visão Geral
                  </div>
                </SelectItem>
                <SelectItem value="reports">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Relatórios
                  </div>
                </SelectItem>
                <SelectItem value="tasks-monitor">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Monitor Tasks
                  </div>
                </SelectItem>
                <SelectItem value="users-manage">
                  <div className="flex items-center gap-2">
                    <UserCog className="w-4 h-4" />
                    Usuários
                  </div>
                </SelectItem>
                <SelectItem value="purchases">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Compras
                  </div>
                </SelectItem>
                <SelectItem value="logs">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Logs
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <TabsList className="grid w-full grid-cols-7">
              <TabsTrigger value="overview" className="gap-2">
                <Shield className="w-4 h-4" />
                Visão Geral
              </TabsTrigger>
              <TabsTrigger value="reports" className="gap-2">
                <BarChart3 className="w-4 h-4" />
                Relatórios
              </TabsTrigger>
              <TabsTrigger value="tasks-monitor" className="gap-2">
                <Activity className="w-4 h-4" />
                Monitor Tasks
              </TabsTrigger>
              <TabsTrigger value="users-manage" className="gap-2">
                <UserCog className="w-4 h-4" />
                Usuários
              </TabsTrigger>
              <TabsTrigger value="purchases" className="gap-2">
                <CreditCard className="w-4 h-4" />
                Compras
              </TabsTrigger>
              <TabsTrigger value="credits" className="gap-2">
                <Coins className="w-4 h-4" />
                Créditos
              </TabsTrigger>
              <TabsTrigger value="logs" className="gap-2">
                <AlertCircle className="w-4 h-4" />
                Logs
              </TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Usuários Recentes</CardTitle>
                  <CardDescription>Últimos 5 usuários cadastrados</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {recentUsers.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">Nenhum usuário encontrado</p>
                    ) : (
                      recentUsers.map((user) => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{user.full_name || "Sem nome"}</p>
                            <p className="text-sm text-muted-foreground truncate">ID: {user.user_id?.slice(0, 8)}...</p>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="font-medium">{user.credits?.available_credits || 0} créditos</span>
                            <span className="text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(user.created_at)}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Tasks Recentes</CardTitle>
                  <CardDescription>Últimas 5 tasks criadas</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {recentTasks.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">Nenhuma task encontrada</p>
                    ) : (
                      recentTasks.map((task) => (
                        <div
                          key={task.id}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{task.product_name}</p>
                            <p className="text-sm text-muted-foreground truncate">
                              {task.profiles?.full_name || "Usuário desconhecido"}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs px-2 py-1 rounded font-medium ${getStatusColor(task.status)}`}>
                              {task.status}
                            </span>
                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(task.created_at)}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Admin 3.0: Metrics and Activity */}
            <MetricTrendChart
              title="Tendência de Atividade"
              description="Usuários ativos e tasks criadas ao longo do tempo"
            />

            <div className="grid gap-4 md:grid-cols-2">
              <AdminActivityFeed />
              <Card>
                <CardHeader>
                  <CardTitle>Resumo do Período</CardTitle>
                  <CardDescription>Estatísticas consolidadas</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm text-muted-foreground">Taxa de Sucesso</span>
                    <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {stats.totalTasks > 0
                        ? Math.round((stats.completedTasks / stats.totalTasks) * 100)
                        : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm text-muted-foreground">Tasks Ativas</span>
                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {stats.activeTasks}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm text-muted-foreground">Falhas Hoje</span>
                    <span className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {stats.failedTasks}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="reports" className="space-y-4">
            <ReportsCharts />
          </TabsContent>

          <TabsContent value="tasks-monitor" className="space-y-4">
            <TaskMonitor />
          </TabsContent>

          <TabsContent value="users-manage" className="space-y-4">
            <UserManager />
          </TabsContent>

          <TabsContent value="purchases" className="space-y-4">
            <PurchasesManager />
          </TabsContent>

          <TabsContent value="credits" className="space-y-4">
            <CreditManagement />
          </TabsContent>

          <TabsContent value="logs" className="space-y-4">
            <ErrorLogsTable />
          </TabsContent>

          <TabsContent value="old-purchases" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Compras Recentes</CardTitle>
                <CardDescription>Últimas 15 compras realizadas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {recentPurchases.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">Nenhuma compra encontrada</p>
                  ) : (
                    recentPurchases.map((purchase) => (
                      <div
                        key={purchase.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{purchase.credits_added} créditos</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {purchase.profiles?.full_name || "Usuário desconhecido"}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-green-600 dark:text-green-400">
                            {formatCurrency(Number(purchase.amount_paid) || 0)}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded font-medium ${getStatusColor(purchase.payment_status)}`}>
                            {purchase.payment_status}
                          </span>
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(purchase.created_at)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}
